import { type SYMBOLCODE, SYMBOLCODE_TO_IMAGES, getPropertyForWeatherSymbol } from "@/geojson";
import { createWfsGeoJSON } from "@/geojson/transform";
import { getMapboxIconSize } from "@/layers/utility/iconSize";
import { applyRoundingFilter, defaultStationAndGridRounding, oneDecimalRounding } from "@/utility/geojsonValueRounding";
import { searchEnginesWeatherParams } from "@/weather-parameters";
import { STATION_MODEL_ID } from "@/weather-parameters/staticStationModels";
import type { GeoJSONFeatureCollection, ModelIdentifier, ParameterUnit } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";

const logger = Logger.fromFilename(__filename);
const WEATHER_SYMBOL_PARAM_PREFIX = "weather_symbol";
const STATION_WEATHER_ICON_SIZE_ADJUST = 1.2;

// Custom behavior: allow station label overlap only when viewing Indonesia.
// This is intentionally non-global to avoid masking base map labels elsewhere.
export const STATION_LABEL_OVERLAP_ZOOM = 3;
// Rough bounding box for Indonesia; update if we need more precise coverage.
export const INDONESIA_BBOX = {
  west: 95,
  south: -11,
  east: 141,
  north: 6,
};

/**
 * A function to transform the parameter unit name in WFS GeoJSON response to a proper format.
 */
export function convertWFSParamUnitToParamUnit(parameter_unit: string) {
  // Due to the XMl formatting and WFS specification, GeoJSON retrieved from
  // the WFS interface replaces the last ":" in the parameter_unit with "_".
  // e.g) (1) WFS -> t_2m_C    (2) Usually -> t_2m:C
  // So we need to convert (1) to (2)
  return parameter_unit.replace(/:(?!.*:)/, "_");
}

/**
 * A function to transform the parameter unit name to a the WFS's parameter unit format.
 */
export function convertParamUnitToWFSParamUnit(parameter_unit: string) {
  // e.g) (1) WFS -> t_2m_C    (2) Usually -> t_2m:C
  // So we need to convert (2) to (1)
  return parameter_unit.replace(":", "_");
}

/**
 * A wrapper function to pass filtering attribute to createWfsGeoJSON.
 * @param parameterUnit
 * @returns A callback for GeoJSONCache.
 */
export function createGeoJSONFormatter(parameterUnit: string) {
  const filteringAttr = convertParamUnitToWFSParamUnit(parameterUnit);
  const isWeatherSymbol = parameterUnit.startsWith(WEATHER_SYMBOL_PARAM_PREFIX);
  return (geoJson: GeoJSONFeatureCollection) => {
    const data = createWfsGeoJSON(geoJson, [filteringAttr]);
    const newFeatures = data.features.map((feature) => {
      if (feature.properties && Object.prototype.hasOwnProperty.call(feature.properties, filteringAttr)) {
        const newProperties = feature.properties;
        const value = feature.properties[filteringAttr];
        newProperties[filteringAttr] = applyRoundingFilter(parameterUnit, value, defaultStationAndGridRounding);
        newProperties.decimalValue = oneDecimalRounding(value);
        if (isWeatherSymbol) {
          const symbolCode = coerceSymbolCode(value);
          Object.assign(newProperties, getPropertyForWeatherSymbol(symbolCode));
        }
        return { ...feature, properties: { ...newProperties } };
      }
      return feature;
    });
    return { ...data, features: newFeatures };
  };
}

export function coerceSymbolCode(value: unknown): SYMBOLCODE {
  const code = Number(value);
  if (Number.isFinite(code) && Object.prototype.hasOwnProperty.call(SYMBOLCODE_TO_IMAGES, code)) {
    return code as SYMBOLCODE;
  }
  return 0;
}

export function getStationWeatherIconSize(textSize: number) {
  const desiredSizePx = textSize * window.devicePixelRatio;
  return getMapboxIconSize(STATION_WEATHER_ICON_SIZE_ADJUST, desiredSizePx);
}

export function isWeatherSymbolParamUnit(parameterUnit: string) {
  return parameterUnit.startsWith(WEATHER_SYMBOL_PARAM_PREFIX);
}

export function getParamDisplayName(paramUnit: ParameterUnit, modelIdentifier: ModelIdentifier) {
  let param: HasLayerIdxNarrowing<PartialWeatherParameter> | null;

  if (modelIdentifier === STATION_MODEL_ID.MIX_OBS) {
    param = searchEnginesWeatherParams.stationObs.matchSomeParameterNameExactly(paramUnit);
  } else if (modelIdentifier === STATION_MODEL_ID.MM_MOS) {
    param = searchEnginesWeatherParams.stationMos.matchSomeParameterNameExactly(paramUnit);
  } else if (modelIdentifier === STATION_MODEL_ID.AARGAU) {
    param = searchEnginesWeatherParams.stationAargau.matchSomeParameterNameExactly(paramUnit);
  } else if (modelIdentifier === STATION_MODEL_ID.NATIONALSTRASSEN) {
    param = searchEnginesWeatherParams.stationNationalstrassen.matchSomeParameterNameExactly(paramUnit);
  } else if (modelIdentifier === STATION_MODEL_ID.ZENTRAS) {
    param = searchEnginesWeatherParams.stationZentras.matchSomeParameterNameExactly(paramUnit);
  } else if (modelIdentifier === STATION_MODEL_ID.GOTTHARD) {
    param = searchEnginesWeatherParams.stationGotthard.matchSomeParameterNameExactly(paramUnit);
  } else if (modelIdentifier === STATION_MODEL_ID.NSNW) {
    param = searchEnginesWeatherParams.stationNsnw.matchSomeParameterNameExactly(paramUnit);
  } else {
    logger.error(`StationLayer should not receive models other than mix-obs and mm-mos. Model:${modelIdentifier}`);
    param = searchEnginesWeatherParams.standard.matchSomeParameterNameExactly(paramUnit);
  }
  return param ? param.parameter.description_en : "";
}
